import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../../context/AuthContext";
import { User2Icon, Copy, CheckCircle2, XCircle } from "lucide-react";
import { User, userService } from "../../../services/UserService";
import { useToastContext } from "../../../context/ToastContext";
import { useLoader } from "../../../context/LoaderContext";
import DataExport from "./DataExport";
import DeleteAccount from "./DeleteAccount";
import AvatarWithBlur from "../../common/AvatarWithBlur";

const ProfileEditPopup: React.FC = () => {
  const { userProfile, setUserProfile } = useAuth();
  const { showToast } = useToastContext();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatar, setAvatar] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (userProfile) {
      setFirstName(userProfile.firstName ?? "");
      setLastName(userProfile.lastName ?? "");
      setPreview(userProfile.avatar ?? "");
    }
  }, [userProfile]);

  const updateUserProfile = async (
    updatedFields: Partial<typeof userProfile>
  ) => {
    const updatedUser = await userService.updateUserProfile(
      updatedFields as Partial<User>
    );
    if (!updatedUser) throw new Error("Failed to update user profile.");
    return updatedUser;
  };

  const handleSave = async () => {
    setLoading(true);
    const updatedProfile: Partial<typeof userProfile> = {};
    let hasUpdates = false;

    if (firstName !== userProfile?.firstName) {
      updatedProfile.firstName = firstName;
      hasUpdates = true;
    }
    if (lastName !== userProfile?.lastName) {
      updatedProfile.lastName = lastName;
      hasUpdates = true;
    }

    if (avatar) {
      try {
        showLoader();
        hasUpdates = true;

        const avatarUrl = await userService.uploadAvatar(avatar, {});

        updatedProfile.avatar = avatarUrl;
      } catch (error) {
        console.error("Error uploading avatar:", error);
        showToast("error", "Error uploading avatar.");
        setLoading(false);
        hideLoader();
        return;
      }
    }

    if (hasUpdates) {
      try {
        showLoader();
        const updatedUser = await updateUserProfile(updatedProfile);
        showToast("success", "Profile updated successfully!");
        setTimeout(() => {
          setLoading(false);
          setUserProfile(updatedUser);
          hideLoader();
        }, 1000);
      } catch (error) {
        console.error("Error updating user profile:", error);
        showToast("error", "Failed to update profile.");
        setLoading(false);
        hideLoader();
      }
    } else {
      showToast("error", "No changes to save.");
      setLoading(false);
      hideLoader();
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const validExtensions = ['.jpg', '.jpeg', '.png'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        showToast("error", "Please select a JPEG or PNG image file.");
        e.target.value = ''; // Reset the input
        return;
      }
      
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleCopyAccountId = () => {
    if (!userProfile?.accountId) return;
    navigator.clipboard.writeText(userProfile.accountId);
    showToast("success", "Copied!");
  };

  const hasChanges =
    firstName !== userProfile?.firstName ||
    lastName !== userProfile?.lastName ||
    avatar !== null;

  return (
    <div className="w-full flex flex-col">
      <div className="bg-background-default dark:bg-background-dark border-b border-divider dark:border-divider-dark">
        <div className="px-6 py-5">
          <h1 className="text-2xl font-semibold text-text-primary dark:text-text-primaryDark mb-1">
            Profile Settings
          </h1>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
            Manage your personal details and account information
          </p>
        </div>
      </div>

      <div className="flex-1 px-6 py-6 overflow-y-auto">
        <div className="space-y-8">
          {/* Profile Section */}
          <div className="bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
            <div className="px-6 py-5 border-b border-divider dark:border-divider-dark">
              <h2 className="text-base font-semibold text-text-primary dark:text-text-primaryDark">
                Profile
              </h2>
              <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
                Update your profile information and avatar
              </p>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-6 mb-6">
                <div className="relative shrink-0">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                  <div className="relative flex items-center">
                    <div className="absolute inset-0 bg-secondary/5 dark:bg-secondary/10 rounded-lg blur-sm opacity-0 hover:opacity-100 transition-opacity duration-200"></div>
                    <div
                      onClick={handleAvatarClick}
                      className="relative flex items-center justify-center rounded-lg p-2 transition-all duration-200 overflow-hidden cursor-pointer bg-background-panel dark:bg-background-paperDark/50 border border-divider/50 dark:border-divider-dark/30 hover:border-divider dark:hover:border-secondary/30 hover:bg-background-default dark:hover:bg-background-paperDark/70"
                    >
                      {preview ? (
                        <AvatarWithBlur 
                          src={preview}
                          alt="Profile"
                          className="w-12 h-12 object-cover rounded-md"
                          fallbackIcon={<User2Icon className="w-6 h-6 text-text-secondary dark:text-text-secondaryDark" />}
                          fallbackClassName="w-12 h-12 rounded-md bg-background-panel dark:bg-secondary/10 flex items-center justify-center"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-background-panel dark:bg-secondary/10 flex items-center justify-center">
                          <User2Icon className="w-6 h-6 text-text-secondary dark:text-text-secondaryDark" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-text-primary dark:text-text-primaryDark mb-1">
                    {userProfile?.firstName || "First"} {userProfile?.lastName || "Last"}
                  </h3>
                  <p className="text-xs text-text-secondary dark:text-text-secondaryDark mb-3">
                    {userProfile?.email || "No email"}
                  </p>
                  <button
                    onClick={handleAvatarClick}
                    className="text-xs text-secondary hover:text-secondary-hover transition-colors"
                  >
                    Change avatar
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-text-primary dark:text-text-primaryDark mb-1.5">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-primary dark:text-text-primaryDark mb-1.5">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-divider dark:border-divider-dark rounded-lg bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition"
                  />
                </div>
              </div>

              {hasChanges && (
                <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-divider dark:border-divider-dark">
                  <button
                    onClick={() => {
                      setFirstName(userProfile?.firstName ?? "");
                      setLastName(userProfile?.lastName ?? "");
                      setAvatar(null);
                      setPreview(userProfile?.avatar ?? "");
                    }}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-black text-white hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-background-paper dark:bg-background-paperDark rounded-lg border border-divider dark:border-divider-dark">
            <div className="px-6 py-5 border-b border-divider dark:border-divider-dark">
              <h2 className="text-base font-semibold text-text-primary dark:text-text-primaryDark">
                Account Information
              </h2>
              <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
                Your account details and verification status
              </p>
            </div>

            <div className="divide-y divide-divider dark:divide-divider-dark">
              <div className="px-6 py-4 flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium text-text-primary dark:text-text-primaryDark mb-0.5">
                    Account ID
                  </div>
                  <div className="text-xs text-text-secondary dark:text-text-secondaryDark font-mono">
                    {userProfile?.accountId || "N/A"}
                  </div>
                </div>
                {userProfile?.accountId && (
                  <button
                    onClick={handleCopyAccountId}
                    className="p-1.5 rounded-md border border-divider dark:border-divider-dark hover:bg-background-panel dark:hover:bg-background-panelDark transition flex-shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark" />
                  </button>
                )}
              </div>

              <div className="px-6 py-4">
                <div className="text-xs font-medium text-text-primary dark:text-text-primaryDark mb-0.5">
                  Email
                </div>
                <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                  {userProfile?.email || "N/A"}
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="text-xs font-medium text-text-primary dark:text-text-primaryDark mb-0.5">
                  Email Verification
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  {userProfile?.emailVerified ? (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-dark dark:text-green-dark" />
                      <span className="text-xs text-green-dark dark:text-green-dark">Verified</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark" />
                      <span className="text-xs text-text-secondary dark:text-text-secondaryDark">Not verified</span>
                    </>
                  )}
                </div>
              </div>

              <div className="px-6 py-4">
                <div className="text-xs font-medium text-text-primary dark:text-text-primaryDark mb-0.5">
                  Last Login
                </div>
                <div className="text-xs text-text-secondary dark:text-text-secondaryDark">
                  {userProfile?.lastLogin
                    ? new Date(userProfile.lastLogin).toLocaleString()
                    : "Never"}
                </div>
              </div>
            </div>
          </div>

          {/* Data & Privacy */}
          <div>
            <div className="mb-3">
              <h2 className="text-base font-semibold text-text-primary dark:text-text-primaryDark">
                Data & Privacy
              </h2>
              <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
                Manage your data and privacy settings
              </p>
            </div>
            <DataExport />
          </div>

          {/* Danger Zone */}
          <div>
            <div className="mb-3">
              <h2 className="text-base font-semibold text-text-primary dark:text-text-primaryDark">
                Danger Zone
              </h2>
              <p className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1">
                Irreversible and destructive actions
              </p>
            </div>
            <DeleteAccount />
          </div>
        </div>

        {/* Subtle Footer Spacing */}
        <div className="mt-12 pt-8 border-t border-divider/30 dark:border-divider-dark/30">
          <div className="flex items-center justify-center gap-2 text-xs text-text-secondary/50 dark:text-text-secondaryDark/50">
            <span>Forgeworks</span>
            <span>•</span>
            <span>v1.0.0</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditPopup;
